import { useMemo } from 'react';
import {
  GridColDef,
  GridValidRowModel,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { useUserSettings } from '../../utils/use-user-settings';
import { GridStateKey } from '../../../types';

interface UseGridColumnsReturn<T extends GridValidRowModel> {
  initialState: GridInitialState | undefined;
  computedColumns: GridColDef<T>[];
}

export default function useGridColumns<T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  gridStateKey: GridStateKey = 'reportsGridStates'
): UseGridColumnsReturn<T> {
  const { viewType } = useUserSettings();

  const activeGridState = useMemo(() => {
    const gridStates = viewType[gridStateKey];
    return gridStates?.activeGridState || null;
  }, [viewType, gridStateKey]);

  const initialState = useMemo(() => {
    if (!activeGridState) return undefined;

    const gridStates = viewType[gridStateKey];
    const savedState = gridStates?.savedGridStates?.find(
      (state) => state.name === activeGridState
    );

    return savedState
      ? (JSON.parse(savedState.value) as GridInitialState)
      : undefined;
  }, [activeGridState, viewType, gridStateKey]);

  const computedColumns = useMemo(() => {
    if (!initialState?.columns?.orderedFields) {
      return columns;
    }

    try {
      const orderedFields = initialState.columns.orderedFields;
      const columnMap = new Map(columns.map((col) => [col.field, col]));
      const reorderedColumns: GridColDef<T>[] = [];

      for (const fieldName of orderedFields) {
        const column = columnMap.get(fieldName);
        if (column) {
          reorderedColumns.push(column);
        }
      }

      columns.forEach((column) => {
        if (!orderedFields.includes(column.field)) {
          reorderedColumns.push(column);
        }
      });

      return reorderedColumns;
    } catch {
      return columns;
    }
  }, [columns, initialState?.columns?.orderedFields]);

  return {
    initialState,
    computedColumns,
  };
}
